import { useState, useEffect, useCallback } from 'react';
import { ref, get, set } from 'firebase/database';
import {
  HiOutlineCurrencyDollar,
  HiOutlineTrendingUp,
  HiOutlineCalculator,
  HiOutlineSave,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineArrowNarrowUp,
  HiOutlineArrowNarrowDown,
} from 'react-icons/hi';
import { db } from '../firebase';

function getFinanceRef(uid) {
  return uid ? `Users/${uid}/financeSimulator/inputs` : null;
}

function getGoalsRef(uid) {
  return uid ? `Users/${uid}/financeSimulator/goals` : null;
}

const DEFAULT_INPUTS = {
  monthly: {
    income: '',
    expenses: '',
    investment: '',
    investmentGrowthRate: '',
    expenseIncreaseRate: '',
    customItems: [],
  },
  yearly: {
    salaryIncreaseRate: '',
    customItems: [],
  },
  oneTime: {
    customItems: [],
  },
};

const DEFAULT_GOALS = {
  savingsGoalAmount: '',
  goalDeadlineMonths: '',
  retirementMonthlyExpense: '',
};

const MONTHLY_FIELDS = [
  { key: 'income', label: 'Income', prefix: '$', suffix: '', placeholder: '5000' },
  { key: 'expenses', label: 'Expenses', prefix: '$', suffix: '', placeholder: '3000' },
  { key: 'investment', label: 'Investment', prefix: '$', suffix: '', placeholder: '500' },
  { key: 'investmentGrowthRate', label: 'Investment growth rate', prefix: '', suffix: '%', placeholder: '0.8' },
  { key: 'expenseIncreaseRate', label: 'Expense increase rate', prefix: '', suffix: '%', placeholder: '0.2' },
];

const YEARLY_FIELDS = [
  { key: 'salaryIncreaseRate', label: 'Salary increase rate', prefix: '', suffix: '%', placeholder: '5' },
];

const GOAL_TABS = [
  { id: 'time', label: 'Time to Goal', Icon: HiOutlineClock },
  { id: 'extra', label: 'Extra Needed', Icon: HiOutlineTrendingUp },
  { id: 'retire', label: 'Retirement', Icon: HiOutlineCalendar },
];

function parseNum(val) {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function formatCurrency(amount) {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toFixed(2)}`;
}

function formatMonths(months) {
  if (months < 1) return 'Less than a month';
  const y = Math.floor(months / 12);
  const m = Math.round(months % 12);
  if (y === 0) return `${m} month${m !== 1 ? 's' : ''}`;
  if (m === 0) return `${y} year${y !== 1 ? 's' : ''}`;
  return `${y}y ${m}m`;
}

// Aggregate custom items by type for a given frequency group
function sumCustom(customItems, type) {
  if (!Array.isArray(customItems)) return 0;
  return customItems
    .filter(item => item.type === type)
    .reduce((sum, item) => sum + parseNum(item.amount), 0);
}

// Build effective monthly values from the categorized inputs
function getEffective(inputs) {
  const m = inputs.monthly || {};
  const y = inputs.yearly || {};
  const o = inputs.oneTime || {};

  const monthlyCustomIncome = sumCustom(m.customItems, 'income');
  const monthlyCustomExpense = sumCustom(m.customItems, 'expense');
  const yearlyCustomIncome = sumCustom(y.customItems, 'income');
  const yearlyCustomExpense = sumCustom(y.customItems, 'expense');
  const oneTimeIncome = sumCustom(o.customItems, 'income');
  const oneTimeExpense = sumCustom(o.customItems, 'expense');

  return {
    monthlyIncome: parseNum(m.income) + monthlyCustomIncome + yearlyCustomIncome / 12,
    monthlyExpenses: parseNum(m.expenses) + monthlyCustomExpense + yearlyCustomExpense / 12,
    monthlyInvestment: parseNum(m.investment),
    investmentGrowthRate: parseNum(m.investmentGrowthRate),
    annualSalaryIncreaseRate: parseNum(y.salaryIncreaseRate),
    monthlyExpenseIncreaseRate: parseNum(m.expenseIncreaseRate),
    initialWealth: oneTimeIncome - oneTimeExpense,
  };
}

// Simulate savings accumulation month-by-month
function simulateSavings(inputs, months) {
  const eff = getEffective(inputs);
  let totalSavings = Math.max(0, eff.initialWealth);
  let totalInvestments = 0;
  let monthlyIncome = eff.monthlyIncome;
  let monthlyExpenses = eff.monthlyExpenses;
  const investmentGrowthRate = eff.investmentGrowthRate / 100;
  const annualSalaryIncreaseRate = eff.annualSalaryIncreaseRate / 100;
  const monthlyExpenseIncreaseRate = eff.monthlyExpenseIncreaseRate / 100;

  for (let i = 0; i < months; i++) {
    if (i > 0 && i % 12 === 0) monthlyIncome *= (1 + annualSalaryIncreaseRate);
    if (i > 0) monthlyExpenses *= (1 + monthlyExpenseIncreaseRate);
    totalInvestments *= (1 + investmentGrowthRate);
    totalInvestments += eff.monthlyInvestment;
    const monthlySaving = monthlyIncome - monthlyExpenses - eff.monthlyInvestment;
    totalSavings += Math.max(0, monthlySaving);
  }

  return { totalSavings, totalInvestments, totalWealth: totalSavings + totalInvestments };
}

function calcTimeToGoal(inputs, goalAmount) {
  if (goalAmount <= 0) return null;
  const eff = getEffective(inputs);
  let totalSavings = Math.max(0, eff.initialWealth);
  let totalInvestments = 0;
  let monthlyIncome = eff.monthlyIncome;
  let monthlyExpenses = eff.monthlyExpenses;
  const investmentGrowthRate = eff.investmentGrowthRate / 100;
  const annualSalaryIncreaseRate = eff.annualSalaryIncreaseRate / 100;
  const monthlyExpenseIncreaseRate = eff.monthlyExpenseIncreaseRate / 100;

  if (totalSavings >= goalAmount) return 0;

  const maxMonths = 1200;
  for (let i = 0; i < maxMonths; i++) {
    if (i > 0 && i % 12 === 0) monthlyIncome *= (1 + annualSalaryIncreaseRate);
    if (i > 0) monthlyExpenses *= (1 + monthlyExpenseIncreaseRate);
    totalInvestments *= (1 + investmentGrowthRate);
    totalInvestments += eff.monthlyInvestment;
    const monthlySaving = monthlyIncome - monthlyExpenses - eff.monthlyInvestment;
    totalSavings += Math.max(0, monthlySaving);
    if (totalSavings + totalInvestments >= goalAmount) return i + 1;
  }
  return null;
}

function calcExtraNeeded(inputs, goalAmount, deadlineMonths) {
  if (goalAmount <= 0 || deadlineMonths <= 0) return null;
  const sim = simulateSavings(inputs, deadlineMonths);
  const shortfall = goalAmount - sim.totalWealth;
  if (shortfall <= 0) return { extraMonthly: 0, alreadyReachable: true };
  return { extraMonthly: shortfall / deadlineMonths, alreadyReachable: false };
}

function calcRetirement(inputs, monthlyExpense) {
  if (monthlyExpense <= 0) return null;
  const eff = getEffective(inputs);
  const investmentGrowthRate = eff.investmentGrowthRate / 100;
  const monthlyExpenseIncreaseRate = eff.monthlyExpenseIncreaseRate / 100;

  const simNow = simulateSavings(inputs, 0);
  const sim5y = simulateSavings(inputs, 60);
  const sim10y = simulateSavings(inputs, 120);
  const sim20y = simulateSavings(inputs, 240);

  function survivalMonths(corpus) {
    if (corpus <= 0) return 0;
    let remaining = corpus;
    let expense = monthlyExpense;
    const maxMonths = 1200;
    for (let i = 0; i < maxMonths; i++) {
      remaining *= (1 + investmentGrowthRate);
      if (i > 0) expense *= (1 + monthlyExpenseIncreaseRate);
      remaining -= expense;
      if (remaining <= 0) return i + 1;
    }
    return maxMonths;
  }

  return {
    monthlyExpense,
    expenseIncreaseRate: eff.monthlyExpenseIncreaseRate,
    scenarios: [
      { label: 'Retire now', corpus: simNow.totalWealth, months: survivalMonths(simNow.totalWealth) },
      { label: 'In 5 years', corpus: sim5y.totalWealth, months: survivalMonths(sim5y.totalWealth) },
      { label: 'In 10 years', corpus: sim10y.totalWealth, months: survivalMonths(sim10y.totalWealth) },
      { label: 'In 20 years', corpus: sim20y.totalWealth, months: survivalMonths(sim20y.totalWealth) },
    ],
  };
}

// Ensure loaded data has the right shape (handles old flat format gracefully)
function normalizeInputs(data) {
  if (!data) return DEFAULT_INPUTS;
  if (data.monthly && typeof data.monthly === 'object') {
    return {
      monthly: {
        ...DEFAULT_INPUTS.monthly,
        ...data.monthly,
        customItems: Array.isArray(data.monthly?.customItems) ? data.monthly.customItems : [],
      },
      yearly: {
        ...DEFAULT_INPUTS.yearly,
        ...data.yearly,
        customItems: Array.isArray(data.yearly?.customItems) ? data.yearly.customItems : [],
      },
      oneTime: {
        ...DEFAULT_INPUTS.oneTime,
        ...data.oneTime,
        customItems: Array.isArray(data.oneTime?.customItems) ? data.oneTime.customItems : [],
      },
    };
  }
  return {
    monthly: {
      income: data.monthlyIncome || '',
      expenses: data.monthlyExpenses || '',
      investment: data.monthlyInvestment || '',
      investmentGrowthRate: data.investmentGrowthRate || '',
      expenseIncreaseRate: data.monthlyExpenseIncreaseRate || '',
      customItems: [],
    },
    yearly: {
      salaryIncreaseRate: data.annualSalaryIncreaseRate || '',
      customItems: [],
    },
    oneTime: {
      customItems: [],
    },
  };
}

function FieldInput({ label, prefix, suffix, placeholder, value, onChange }) {
  return (
    <div className="finance-field">
      <label className="finance-label">{label}</label>
      <div className="finance-input-wrap">
        {prefix && <span className="finance-input-prefix">{prefix}</span>}
        <input
          type="number"
          step="any"
          className="finance-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {suffix && <span className="finance-input-suffix">{suffix}</span>}
      </div>
    </div>
  );
}

function CustomItemRow({ item, onChange, onRemove }) {
  return (
    <div className="finance-custom-row">
      <input
        type="text"
        className="finance-custom-name"
        value={item.name}
        onChange={e => onChange({ ...item, name: e.target.value })}
        placeholder="Category name"
      />
      <select
        className="finance-custom-type"
        value={item.type}
        onChange={e => onChange({ ...item, type: e.target.value })}
      >
        <option value="income">Income</option>
        <option value="expense">Expense</option>
      </select>
      <div className="finance-input-wrap finance-custom-amount-wrap">
        <span className="finance-input-prefix">$</span>
        <input
          type="number"
          step="any"
          className="finance-input"
          value={item.amount}
          onChange={e => onChange({ ...item, amount: e.target.value })}
          placeholder="0"
        />
      </div>
      <button type="button" className="finance-custom-remove" onClick={onRemove} title="Remove">
        <HiOutlineTrash aria-hidden />
      </button>
    </div>
  );
}

// Stat card for the effective summary
function StatCard({ label, value, icon: Icon, variant }) {
  return (
    <div className={`finance-stat ${variant ? `finance-stat--${variant}` : ''}`}>
      <div className="finance-stat-icon-wrap">
        <Icon className="finance-stat-icon" aria-hidden />
      </div>
      <div className="finance-stat-content">
        <span className="finance-stat-label">{label}</span>
        <span className="finance-stat-value">{value}</span>
      </div>
    </div>
  );
}

// Progress bar used in retirement scenarios
function SurvivalBar({ months, maxMonths }) {
  const pct = Math.min(100, (months / maxMonths) * 100);
  const isMax = months >= 1200;
  return (
    <div className="finance-survival-bar">
      <div
        className={`finance-survival-fill ${isMax ? 'finance-survival-fill--max' : ''}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function FinanceSimulator({ user }) {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [goals, setGoals] = useState(DEFAULT_GOALS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeGoal, setActiveGoal] = useState(null);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [inputSnap, goalSnap] = await Promise.all([
          get(ref(db, getFinanceRef(uid))),
          get(ref(db, getGoalsRef(uid))),
        ]);
        if (cancelled) return;
        if (inputSnap.exists()) setInputs(normalizeInputs(inputSnap.val()));
        if (goalSnap.exists()) setGoals({ ...DEFAULT_GOALS, ...goalSnap.val() });
      } catch (err) {
        console.error('Failed to load finance data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const updateField = (group, key, value) => {
    setInputs(prev => ({
      ...prev,
      [group]: { ...prev[group], [key]: value },
    }));
    setSaveStatus(null);
  };

  const addCustomItem = (group) => {
    setInputs(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        customItems: [...(prev[group].customItems || []), { name: '', amount: '', type: 'income' }],
      },
    }));
    setSaveStatus(null);
  };

  const updateCustomItem = (group, index, item) => {
    setInputs(prev => {
      const items = [...(prev[group].customItems || [])];
      items[index] = item;
      return { ...prev, [group]: { ...prev[group], customItems: items } };
    });
    setSaveStatus(null);
  };

  const removeCustomItem = (group, index) => {
    setInputs(prev => {
      const items = [...(prev[group].customItems || [])];
      items.splice(index, 1);
      return { ...prev, [group]: { ...prev[group], customItems: items } };
    });
    setSaveStatus(null);
  };

  const handleGoalChange = (key, value) => {
    setGoals(prev => ({ ...prev, [key]: value }));
    setSaveStatus(null);
  };

  const handleSave = useCallback(async () => {
    const uid = user?.uid;
    if (!uid) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      await Promise.all([
        set(ref(db, getFinanceRef(uid)), inputs),
        set(ref(db, getGoalsRef(uid)), goals),
      ]);
      setSaveStatus('saved');
    } catch (err) {
      console.error('Failed to save finance data:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [user?.uid, inputs, goals]);

  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    setGoals(DEFAULT_GOALS);
    setActiveGoal(null);
    setSaveStatus(null);
  };

  const eff = getEffective(inputs);
  const hasInputs = eff.monthlyIncome > 0;
  const netSavings = eff.monthlyIncome - eff.monthlyExpenses - eff.monthlyInvestment;
  const timeToGoal = activeGoal === 'time' && hasInputs ? calcTimeToGoal(inputs, parseNum(goals.savingsGoalAmount)) : null;
  const extraNeeded = activeGoal === 'extra' && hasInputs ? calcExtraNeeded(inputs, parseNum(goals.savingsGoalAmount), parseNum(goals.goalDeadlineMonths)) : null;
  const retirement = activeGoal === 'retire' && hasInputs ? calcRetirement(inputs, parseNum(goals.retirementMonthlyExpense)) : null;

  // Find max months across retirement scenarios for scaling bars
  const retireMaxMonths = retirement
    ? Math.max(...retirement.scenarios.map(s => s.months), 1)
    : 1;

  if (loading) {
    return (
      <div className="page page--full-width finance-page-wrap">
        <div className="finance-page">
          <div className="finance-loading">
            <div className="spinner" aria-label="Loading" />
            <span>Loading your profile...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--full-width finance-page-wrap">
      <div className="finance-page">
        <header className="finance-header">
          <h1 className="finance-title">Finance Simulator</h1>
          <p className="finance-subtitle">Simulate different financial scenarios and plan your goals</p>
        </header>

        <div className="finance-columns">
          {/* Left column: Financial Profile */}
          <div className="finance-col finance-col--left">
            {/* Monthly */}
            <section className="finance-section">
              <h2 className="finance-section-title">
                <span className="finance-section-icon-wrap finance-section-icon-wrap--blue">
                  <HiOutlineClock className="finance-section-icon" aria-hidden />
                </span>
                Monthly
              </h2>
              <div className="finance-grid">
                {MONTHLY_FIELDS.map(f => (
                  <FieldInput
                    key={f.key}
                    label={f.label}
                    prefix={f.prefix}
                    suffix={f.suffix}
                    placeholder={f.placeholder}
                    value={inputs.monthly[f.key]}
                    onChange={v => updateField('monthly', f.key, v)}
                  />
                ))}
              </div>
              {(inputs.monthly.customItems || []).length > 0 && (
                <div className="finance-custom-list">
                  {inputs.monthly.customItems.map((item, i) => (
                    <CustomItemRow
                      key={i}
                      item={item}
                      onChange={updated => updateCustomItem('monthly', i, updated)}
                      onRemove={() => removeCustomItem('monthly', i)}
                    />
                  ))}
                </div>
              )}
              <button type="button" className="finance-add-btn" onClick={() => addCustomItem('monthly')}>
                <HiOutlinePlus aria-hidden /> Add monthly category
              </button>
            </section>

            {/* Yearly */}
            <section className="finance-section">
              <h2 className="finance-section-title">
                <span className="finance-section-icon-wrap finance-section-icon-wrap--purple">
                  <HiOutlineCalendar className="finance-section-icon" aria-hidden />
                </span>
                Yearly
              </h2>
              <div className="finance-grid">
                {YEARLY_FIELDS.map(f => (
                  <FieldInput
                    key={f.key}
                    label={f.label}
                    prefix={f.prefix}
                    suffix={f.suffix}
                    placeholder={f.placeholder}
                    value={inputs.yearly[f.key]}
                    onChange={v => updateField('yearly', f.key, v)}
                  />
                ))}
              </div>
              {(inputs.yearly.customItems || []).length > 0 && (
                <div className="finance-custom-list">
                  {inputs.yearly.customItems.map((item, i) => (
                    <CustomItemRow
                      key={i}
                      item={item}
                      onChange={updated => updateCustomItem('yearly', i, updated)}
                      onRemove={() => removeCustomItem('yearly', i)}
                    />
                  ))}
                </div>
              )}
              <button type="button" className="finance-add-btn" onClick={() => addCustomItem('yearly')}>
                <HiOutlinePlus aria-hidden /> Add yearly category
              </button>
            </section>

            {/* One-time */}
            <section className="finance-section">
              <h2 className="finance-section-title">
                <span className="finance-section-icon-wrap finance-section-icon-wrap--green">
                  <HiOutlineCurrencyDollar className="finance-section-icon" aria-hidden />
                </span>
                One-time
              </h2>
              <p className="finance-section-desc">Lump sums like inheritance, gifts, large purchases, or debts.</p>
              {(inputs.oneTime.customItems || []).length > 0 && (
                <div className="finance-custom-list">
                  {inputs.oneTime.customItems.map((item, i) => (
                    <CustomItemRow
                      key={i}
                      item={item}
                      onChange={updated => updateCustomItem('oneTime', i, updated)}
                      onRemove={() => removeCustomItem('oneTime', i)}
                    />
                  ))}
                </div>
              )}
              <button type="button" className="finance-add-btn" onClick={() => addCustomItem('oneTime')}>
                <HiOutlinePlus aria-hidden /> Add one-time item
              </button>
            </section>

            {/* Save / Reset */}
            <div className="finance-actions">
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <HiOutlineSave aria-hidden /> {saving ? 'Saving...' : 'Save Profile'}
              </button>
              <button type="button" className="btn btn-outline" onClick={handleReset}>
                <HiOutlineRefresh aria-hidden /> Reset
              </button>
              {saveStatus === 'saved' && (
                <span className="finance-save-status finance-save-status--success">
                  <HiOutlineCheckCircle aria-hidden /> Saved
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="finance-save-status finance-save-status--error">Failed to save</span>
              )}
            </div>
          </div>

          {/* Right column: Goal Simulators */}
          <div className="finance-col finance-col--right">
            {/* Effective summary as stat cards */}
            {hasInputs && (
              <div className="finance-stats">
                <StatCard
                  label="Monthly Income"
                  value={formatCurrency(eff.monthlyIncome)}
                  icon={HiOutlineArrowNarrowUp}
                  variant="income"
                />
                <StatCard
                  label="Monthly Expenses"
                  value={formatCurrency(eff.monthlyExpenses)}
                  icon={HiOutlineArrowNarrowDown}
                  variant="expense"
                />
                <StatCard
                  label="Net Savings"
                  value={formatCurrency(Math.abs(netSavings))}
                  icon={HiOutlineTrendingUp}
                  variant={netSavings >= 0 ? 'positive' : 'negative'}
                />
                {eff.initialWealth !== 0 && (
                  <StatCard
                    label="Starting Wealth"
                    value={formatCurrency(eff.initialWealth)}
                    icon={HiOutlineCurrencyDollar}
                    variant="wealth"
                  />
                )}
              </div>
            )}

            <section className="finance-section finance-section--goals">
              <h2 className="finance-section-title">
                <span className="finance-section-icon-wrap finance-section-icon-wrap--accent">
                  <HiOutlineCalculator className="finance-section-icon" aria-hidden />
                </span>
                Goal Simulators
              </h2>

              {!hasInputs && (
                <div className="finance-empty">
                  <HiOutlineCalculator className="finance-empty-icon" aria-hidden />
                  <p className="finance-empty-text">Fill in your financial profile to use the simulators.</p>
                </div>
              )}

              {hasInputs && (
                <>
                  <div className="finance-goal-tabs">
                    {GOAL_TABS.map(tab => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`finance-goal-tab ${activeGoal === tab.id ? 'finance-goal-tab--active' : ''}`}
                        onClick={() => setActiveGoal(activeGoal === tab.id ? null : tab.id)}
                      >
                        <tab.Icon className="finance-goal-tab-icon" aria-hidden />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeGoal === 'time' && (
                    <div className="finance-goal-panel">
                      <p className="finance-goal-desc">How long will it take to reach a savings goal?</p>
                      <div className="finance-goal-input-row">
                        <label className="finance-label">Target amount</label>
                        <div className="finance-input-wrap">
                          <span className="finance-input-prefix">$</span>
                          <input type="number" step="any" className="finance-input" value={goals.savingsGoalAmount} onChange={e => handleGoalChange('savingsGoalAmount', e.target.value)} placeholder="100000" />
                        </div>
                      </div>
                      {timeToGoal !== null && (
                        <div className="finance-result finance-result--accent">
                          <div className="finance-result-value">{formatMonths(timeToGoal)}</div>
                          <div className="finance-result-label">to reach {formatCurrency(parseNum(goals.savingsGoalAmount))}</div>
                        </div>
                      )}
                      {timeToGoal === null && parseNum(goals.savingsGoalAmount) > 0 && (
                        <div className="finance-result finance-result--warn">
                          <div className="finance-result-value">Unreachable</div>
                          <div className="finance-result-label">With current finances, this goal cannot be reached within 100 years</div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeGoal === 'extra' && (
                    <div className="finance-goal-panel">
                      <p className="finance-goal-desc">How much extra do you need to earn or save to reach a goal in a specific time?</p>
                      <div className="finance-goal-inputs">
                        <div className="finance-goal-input-row">
                          <label className="finance-label">Target amount</label>
                          <div className="finance-input-wrap">
                            <span className="finance-input-prefix">$</span>
                            <input type="number" step="any" className="finance-input" value={goals.savingsGoalAmount} onChange={e => handleGoalChange('savingsGoalAmount', e.target.value)} placeholder="100000" />
                          </div>
                        </div>
                        <div className="finance-goal-input-row">
                          <label className="finance-label">Deadline (months)</label>
                          <div className="finance-input-wrap">
                            <input type="number" step="1" className="finance-input" value={goals.goalDeadlineMonths} onChange={e => handleGoalChange('goalDeadlineMonths', e.target.value)} placeholder="24" />
                            <span className="finance-input-suffix">months</span>
                          </div>
                        </div>
                      </div>
                      {extraNeeded && extraNeeded.alreadyReachable && (
                        <div className="finance-result finance-result--success">
                          <div className="finance-result-value">Already on track</div>
                          <div className="finance-result-label">You will reach {formatCurrency(parseNum(goals.savingsGoalAmount))} within {formatMonths(parseNum(goals.goalDeadlineMonths))} with your current plan</div>
                        </div>
                      )}
                      {extraNeeded && !extraNeeded.alreadyReachable && (
                        <div className="finance-result finance-result--accent">
                          <div className="finance-result-value">{formatCurrency(extraNeeded.extraMonthly)}/mo extra</div>
                          <div className="finance-result-label">You need an additional {formatCurrency(extraNeeded.extraMonthly)} per month to reach {formatCurrency(parseNum(goals.savingsGoalAmount))} in {formatMonths(parseNum(goals.goalDeadlineMonths))}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeGoal === 'retire' && (
                    <div className="finance-goal-panel">
                      <p className="finance-goal-desc">How long will your money last in retirement?</p>
                      <div className="finance-goal-input-row">
                        <label className="finance-label">Expected monthly expense in retirement</label>
                        <div className="finance-input-wrap">
                          <span className="finance-input-prefix">$</span>
                          <input type="number" step="any" className="finance-input" value={goals.retirementMonthlyExpense} onChange={e => handleGoalChange('retirementMonthlyExpense', e.target.value)} placeholder="3000" />
                        </div>
                      </div>
                      {retirement && (
                        <div className="finance-retirement-results">
                          {retirement.expenseIncreaseRate > 0 && (
                            <p className="finance-retire-note">
                              Expenses increase at {retirement.expenseIncreaseRate}%/mo (from your profile)
                            </p>
                          )}
                          <div className="finance-retire-scenarios">
                            {retirement.scenarios.map((s, i) => (
                              <div key={i} className="finance-retire-card">
                                <div className="finance-retire-card-header">
                                  <span className="finance-retire-card-label">{s.label}</span>
                                  <span className="finance-retire-card-corpus">{formatCurrency(s.corpus)}</span>
                                </div>
                                <SurvivalBar months={s.months} maxMonths={retireMaxMonths} />
                                <div className="finance-retire-card-footer">
                                  <span className="finance-retire-card-duration">
                                    {s.months >= 1200 ? '100+ years' : formatMonths(s.months)}
                                  </span>
                                  <span className="finance-retire-card-expense">
                                    at {formatCurrency(retirement.monthlyExpense)}/mo
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeGoal && (
                    <div className="finance-actions" style={{ marginTop: '1rem' }}>
                      <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        <HiOutlineSave aria-hidden /> {saving ? 'Saving...' : 'Save All'}
                      </button>
                      {saveStatus === 'saved' && (
                        <span className="finance-save-status finance-save-status--success">
                          <HiOutlineCheckCircle aria-hidden /> Saved
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FinanceSimulator;
